import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, UploadCloud, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/admin/Field";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

export default function MediaPage() {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/uploads");
      setFiles(data?.files || []);
    } catch {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleFiles(filesList) {
    if (!filesList?.length) return;
    setUploading(true);
    try {
      for (const file of filesList) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${filesList.length} file(s) uploaded`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/uploads/${pendingDelete.filename}`);
      setFiles((arr) => arr.filter((f) => f.filename !== pendingDelete.filename));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setPendingDelete(null);
    }
  }

  function copyUrl(url) {
    navigator.clipboard?.writeText(url);
    toast.success("URL copied");
  }

  return (
    <div className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#0055ff]">Media</span>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight text-white md:text-4xl">Uploaded files</h1>
          <p className="mt-1 font-body text-sm text-white/50">Anything you upload from any CMS form lands here.</p>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="media-upload-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white transition hover:bg-[#1a66ff] disabled:opacity-60"
        >
          <UploadCloud className="h-4 w-4" />
          {uploading ? "Uploading…" : "Upload images"}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif"
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : files.length === 0 ? (
        <EmptyState
          icon={ImagePlus}
          title="No uploads yet"
          description="Drop images here or use the upload button."
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
          {files.map((f) => (
            <div
              key={f.filename}
              className="group relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0a0a0e]"
            >
              <img src={f.url} alt="" className="h-32 w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 transition group-hover:opacity-100" />
              <div className="absolute inset-x-2 bottom-2 flex gap-2 opacity-0 transition group-hover:opacity-100">
                <button
                  onClick={() => copyUrl(f.url)}
                  className="flex flex-1 items-center justify-center gap-1 rounded-full bg-white/10 px-2 py-1 font-body text-[10px] text-white hover:bg-white/20"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
                <button
                  onClick={() => setPendingDelete(f)}
                  className="flex items-center justify-center rounded-full bg-red-500/80 p-1.5 text-white hover:bg-red-500"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this file?"
        description={`${pendingDelete?.filename || ""} will be removed from disk.`}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
